import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Input, Select, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  AdvancedFiltersPanelWrapper,
  AdvancedFiltersPanelHeader,
  AdvancedFiltersPanelTitle,
  AdvancedFiltersFieldsRow,
  AdvancedFilterField,
  AdvancedFiltersActions,
  ActionLabel,
  Dropdown
} from './styled';

const { Option } = Select;

// Activity-tab body. Renders a config-driven facet row using the same
// styled-component shells as the Transaction body so visual parity holds.
const ActivityFiltersBody = ({ filterFields, filters, onApply, onClose }) => {
  const initialDraft = useMemo(() => {
    const d = {};
    filterFields.forEach((f) => {
      d[f.key] = filters?.advancedFilters?.[f.key] || undefined;
    });
    return d;
  }, [filterFields, filters]);
  const [draft, setDraft] = useState(initialDraft);

  useEffect(() => {
    setDraft(initialDraft);
  }, [initialDraft]);

  const handleApply = () => {
    const out = {};
    filterFields.forEach((f) => {
      if (draft[f.key]) out[f.key] = draft[f.key];
    });
    onApply({ advancedFilters: out });
    onClose && onClose();
  };

  const handleClear = () => {
    const cleared = {};
    filterFields.forEach((f) => {
      cleared[f.key] = undefined;
    });
    setDraft(cleared);
    onApply({ advancedFilters: {} });
    onClose && onClose();
  };

  const isDefault = filterFields.every((f) => !draft[f.key]);

  return (
    <AdvancedFiltersPanelWrapper>
      <AdvancedFiltersPanelHeader>
        <AdvancedFiltersPanelTitle>Advanced Filters</AdvancedFiltersPanelTitle>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          size="small"
        />
      </AdvancedFiltersPanelHeader>
      <AdvancedFiltersFieldsRow>
        {filterFields.map((field) => (
          <AdvancedFilterField key={field.key}>
            <ActionLabel>{field.label}</ActionLabel>
            <Select
              value={draft[field.key] || ''}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  [field.key]: value || undefined
                }))
              }
              // FIN-12131: keep the popup at the control width (never wider than
              // the field, so it can't spill past the panel border) and wrap long
              // option text within it via dropdownClassName.
              dropdownClassName="adv-filter-select-dropdown"
              style={{ width: '100%' }}>
              <Option value="">All</Option>
              {(field.options || []).map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </AdvancedFilterField>
        ))}
      </AdvancedFiltersFieldsRow>
      <AdvancedFiltersActions>
        <Button onClick={handleClear} disabled={isDefault}>
          Clear Filters
        </Button>
        <Button type="primary" onClick={handleApply}>
          Apply
        </Button>
      </AdvancedFiltersActions>
    </AdvancedFiltersPanelWrapper>
  );
};

ActivityFiltersBody.propTypes = {
  filterFields: PropTypes.array.isRequired,
  filters: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func
};

// Options may be plain strings or { code/value, description/label } objects.
const toSelectOptions = (list) => [
  { value: '', label: 'All' },
  ...(Array.isArray(list) ? list : []).map((t) => ({
    value: typeof t === 'string' ? t : t.code || t.value || t,
    label: typeof t === 'string' ? t : t.description || t.label || t.code || t
  }))
];

// Blank or whitespace-only typed text means no filter. A dropdown sends the value it offered,
// padding included: the BE matches it exactly.
const appliedValue = (value, { options } = {}) =>
  (!options && typeof value === 'string' ? value.trim() : value) || undefined;

const isAllRule = (value) => !value || value === 'all';

const AdvancedFiltersPanel = ({
  filters,
  onApply,
  onClose,
  typologies = [],
  transactionTypes = [],
  isTriggeredOnly,
  isTMTransaction = true,
  hideAccountId = false,
  hideTypologies = false,
  filterFields,
  columnFilters,
  // FIN-11542 — FRAML-P variant filter props
  isFramlP = false,
  variants = [],
  variantId
}) => {
  // Activity-tab variant: caller passes a config-driven `filterFields` array of
  // shape { key, label, options: [{value, label}] }. When provided, render
  // exactly those facets (Activity Type, Activity Status, Channel) instead of
  // the Transaction-tab defaults.
  if (Array.isArray(filterFields) && filterFields.length) {
    return (
      <ActivityFiltersBody
        filterFields={filterFields}
        filters={filters}
        onApply={onApply}
        onClose={onClose}
      />
    );
  }
  // FIN-13748 — the FRAML canonical grids pass `columnFilters` so every filter acts on a visible
  // column; every other grid keeps the Account Number + Transaction Type pair.
  const fields = columnFilters || [
    ...(hideAccountId ? [] : [{ key: 'accountId', label: 'Account Number' }]),
    {
      key: 'transactionType',
      label: 'Transaction Type',
      options: transactionTypes
    }
  ];
  const ruleKey = isFramlP ? 'variantId' : 'typologyId';
  const appliedRule = isFramlP ? variantId : filters.typologyId;
  const fieldDraft = (valueOf) =>
    Object.fromEntries(
      fields.map(({ key, options }) => [
        key,
        valueOf(key) || (options ? undefined : '')
      ])
    );
  const draftFromFilters = () => ({
    ...fieldDraft((key) => filters[key]),
    typologyId: filters.typologyId || 'all',
    variantId: variantId || 'all'
  });
  const [draft, setDraft] = useState(draftFromFilters);

  const appliedKey = JSON.stringify(
    fields.map(({ key }) => [key, filters[key] ?? null])
  );
  useEffect(() => {
    setDraft(draftFromFilters());
  }, [appliedKey, filters.typologyId, variantId]);

  const typologiesOptions = useMemo(() => {
    const list = Array.isArray(typologies) ? typologies : [];
    return [
      { id: 'all', name: isTriggeredOnly ? 'All' : '' },
      ...list.map(({ id, name }) => ({ id, name }))
    ];
  }, [typologies, isTriggeredOnly]);

  // FIN-11542 — variant options for FRAML-P dropdown (same shape as typologiesOptions)
  const variantsOptions = useMemo(() => {
    const list = Array.isArray(variants) ? variants : [];
    return [
      { id: 'all', name: isTriggeredOnly ? 'All' : '' },
      ...list.map(({ id, name }) => ({ id, name }))
    ];
  }, [variants, isTriggeredOnly]);

  const isDefault =
    fields.every((field) => !appliedValue(draft[field.key], field)) &&
    isAllRule(draft[ruleKey]);

  const hasChanges =
    fields.some(
      (field) =>
        appliedValue(draft[field.key], field) !==
        appliedValue(filters[field.key], field)
    ) || (draft[ruleKey] || 'all') !== (appliedRule || 'all');

  const isAppliedDefault =
    fields.every(({ key }) => !filters[key]) && isAllRule(appliedRule);

  const handleClear = () => {
    setDraft({ ...fieldDraft(() => undefined), [ruleKey]: 'all' });
    if (!isAppliedDefault) {
      onApply({
        ...Object.fromEntries(fields.map(({ key }) => [key, undefined])),
        [ruleKey]: 'all'
      });
      onClose();
    }
  };

  const handleApply = () => {
    onApply({
      ...Object.fromEntries(
        fields.map((field) => [
          field.key,
          appliedValue(draft[field.key], field)
        ])
      ),
      [ruleKey]: draft[ruleKey] || 'all'
    });
  };

  return (
    <AdvancedFiltersPanelWrapper>
      <AdvancedFiltersPanelHeader>
        <AdvancedFiltersPanelTitle>Advanced Filters</AdvancedFiltersPanelTitle>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          size="small"
        />
      </AdvancedFiltersPanelHeader>

      <AdvancedFiltersFieldsRow>
        {fields.map((field) => (
          <AdvancedFilterField key={field.key}>
            <ActionLabel>{field.label}</ActionLabel>
            {field.options ? (
              <Select
                value={draft[field.key] || ''}
                onChange={(value) =>
                  setDraft((prev) => ({
                    ...prev,
                    [field.key]: value || undefined
                  }))
                }
                // FIN-12131: keep the popup at the control width (never wider than the
                // field, so it can't spill past the panel border) and wrap long option
                // text within it via dropdownClassName.
                dropdownClassName="adv-filter-select-dropdown"
                style={{ width: '100%' }}>
                {toSelectOptions(field.options).map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder={`Filter by ${field.label.toLowerCase()}`}
                value={draft[field.key]}
                onChange={(e) => {
                  // Read before setDraft: the updater can run after React restores the
                  // controlled input, when e.target.value is back to the old value.
                  const { value } = e.target;
                  setDraft((prev) => ({ ...prev, [field.key]: value }));
                }}
                allowClear
              />
            )}
          </AdvancedFilterField>
        ))}

        {isTMTransaction && !hideTypologies && (
          <AdvancedFilterField>
            {isFramlP ? (
              <>
                <ActionLabel>Variants</ActionLabel>
                <Dropdown
                  // FIN-12131: bound the menu to the field width (BaseDropdown
                  // applies `width` to both control + menu inline) so long
                  // Variant options wrap within the field instead of spilling
                  // past the panel's right border.
                  width="100%"
                  disabled={!isTriggeredOnly}
                  dropdownValue={variantsOptions.find(
                    ({ id }) => id === draft.variantId
                  )}
                  defaultValue="all"
                  dropdownOptions={variantsOptions.map((option) => ({
                    ...option,
                    title: option.name
                  }))}
                  handleValueChange={(variant) =>
                    setDraft((prev) => ({ ...prev, variantId: variant.id }))
                  }
                />
              </>
            ) : (
              <>
                <ActionLabel>Typologies</ActionLabel>
                <Dropdown
                  width="100%"
                  disabled={!isTriggeredOnly}
                  dropdownValue={typologiesOptions.find(
                    ({ id }) => id === draft.typologyId
                  )}
                  defaultValue="all"
                  dropdownOptions={typologiesOptions.map((option) => ({
                    ...option,
                    title: option.name
                  }))}
                  handleValueChange={(typology) =>
                    setDraft((prev) => ({ ...prev, typologyId: typology.id }))
                  }
                />
              </>
            )}
          </AdvancedFilterField>
        )}
      </AdvancedFiltersFieldsRow>

      <AdvancedFiltersActions>
        <Button onClick={handleClear} disabled={isDefault}>
          Clear Filters
        </Button>
        <Button type="primary" onClick={handleApply} disabled={!hasChanges}>
          Apply
        </Button>
      </AdvancedFiltersActions>
    </AdvancedFiltersPanelWrapper>
  );
};

AdvancedFiltersPanel.propTypes = {
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  typologies: PropTypes.array,
  transactionTypes: PropTypes.array,
  isTriggeredOnly: PropTypes.bool,
  isTMTransaction: PropTypes.bool,
  filterFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.array
    })
  ),
  // FIN-13748 — replaces the Account Number + Transaction Type pair. A field with `options` is a
  // dropdown, one without is a text input.
  columnFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.array
    })
  ),
  // FIN-11542 — FRAML-P variant filter props
  isFramlP: PropTypes.bool,
  variants: PropTypes.array,
  variantId: PropTypes.string
};

export default AdvancedFiltersPanel;
